import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { toast } from 'sonner';
import { SignOut, MagnifyingGlass, Shuffle, CurrencyDollar } from '@phosphor-icons/react';

const KioskOffice = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [show, setShow] = useState(null);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('ALL');

  useEffect(() => {
    const s = sessionStorage.getItem('kiosk_session');
    if (!s) { navigate('/kiosk'); return; }
    const parsed = JSON.parse(s);
    if (parsed.role !== 'office') { navigate('/kiosk'); return; }
    setSession(parsed);
    setShow(parsed.show);
    if (parsed.show?.classes?.length > 0) setSelectedClassId(parsed.show.classes[0].id);
  }, [navigate]);

  const loadEntries = useCallback(async () => {
    if (!session || !selectedClassId) return;
    try {
      const { data } = await api.get(`/shows/${session.show_id}/classes/${selectedClassId}/entries`);
      setEntries(data);
    } catch {}
  }, [session, selectedClassId]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const togglePayment = async (entry) => {
    const cycle = { UNPAID: 'PAID', PAID: 'COMP', COMP: 'UNPAID', PENDING: 'PAID' };
    const next = cycle[entry.payment_status] || 'PAID';
    try {
      await api.patch(`/shows/${session.show_id}/classes/${selectedClassId}/entries/${entry.id}/payment`, { payment_status: next });
      toast.success(`${entry.rider_name}: ${next}`);
      loadEntries();
    } catch { toast.error('Failed to update payment'); }
  };

  const generateDraw = async () => {
    try {
      await api.post(`/shows/${session.show_id}/classes/${selectedClassId}/draw`);
      toast.success('Draw generated!');
      loadEntries();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate draw'); }
  };

  if (!session || !show) return null;

  const filtered = entries.filter(e => {
    if (search && !e.rider_name.toLowerCase().includes(search.toLowerCase()) && !(e.horse_name || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (paymentFilter !== 'ALL' && e.payment_status !== paymentFilter) return false;
    return true;
  });

  // v248 — Backend already returns entries sorted per class-level
  // draw_reversed flag. Preserve that order; just push unpaid-yet-numberless
  // entries to the end so the running list starts with numbered riders.
  const sorted = [...filtered].sort((a, b) => (a.draw_number == null) - (b.draw_number == null));
  const unpaidCount = entries.filter(e => ['UNPAID', 'PENDING'].includes(e.payment_status)).length;
  const currentClass = show.classes.find(c => c.id === selectedClassId);
  const unpaidTotal = entries.filter(e => ['UNPAID', 'PENDING'].includes(e.payment_status)).reduce((sum, e) => sum + parseFloat(currentClass?.entry_fee || 0), 0);

  const paymentColors = { PAID: 'bg-[#34C759] text-white', UNPAID: 'bg-[#FF3B30] text-white', PENDING: 'bg-[#FF9500] text-white', COMP: 'bg-[#0096FF] text-white' };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="bg-[#161616] border-b border-[#262626] px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div>
          <p className="text-white font-bold text-sm" style={{fontFamily: 'Outfit'}}>{show.name}</p>
          <p className="text-[#0096FF] text-xs font-semibold flex items-center gap-1"><CurrencyDollar size={12} /> Office Staff</p>
        </div>
        <button onClick={() => { sessionStorage.removeItem('kiosk_session'); navigate('/kiosk'); }} className="flex items-center gap-1 text-[#9E9E9E] hover:text-white text-sm">
          <SignOut size={16} /> Exit
        </button>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Class Selector */}
        <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)}
          className="w-full px-4 py-3 bg-[#161616] border border-[#262626] rounded-lg text-white mb-4 focus:outline-none focus:border-[#0096FF]"
          data-testid="office-class-select">
          {show.classes.map(c => (
            <option key={c.id} value={c.id}>{c.name}{c.scheduled_day ? ` (${c.scheduled_day})` : ''} — ${c.entry_fee}</option>
          ))}
        </select>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-2 mb-4">
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 text-center">
            <p className="text-[#9E9E9E] text-[10px]">Total</p>
            <p className="text-xl font-bold text-white">{entries.length}</p>
          </div>
          <div className="bg-[#161616] border border-[#34C759]/30 rounded-lg p-3 text-center">
            <p className="text-[#34C759] text-[10px]">Paid</p>
            <p className="text-xl font-bold text-[#34C759]">{entries.filter(e => e.payment_status === 'PAID').length}</p>
          </div>
          <div className="bg-[#161616] border border-[#FF3B30]/30 rounded-lg p-3 text-center cursor-pointer" onClick={() => setPaymentFilter(paymentFilter === 'UNPAID' ? 'ALL' : 'UNPAID')}>
            <p className="text-[#FF3B30] text-[10px]">Unpaid (${unpaidTotal.toFixed(0)})</p>
            <p className="text-xl font-bold text-[#FF3B30]">{unpaidCount}</p>
          </div>
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 text-center">
            <p className="text-[#0096FF] text-[10px]">Comp</p>
            <p className="text-xl font-bold text-[#0096FF]">{entries.filter(e => e.payment_status === 'COMP').length}</p>
          </div>
        </div>

        {/* Search + Actions */}
        <div className="flex gap-2 mb-4">
          <div className="flex-1 relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search rider or horse..."
              className="w-full pl-9 pr-4 py-2.5 bg-[#161616] border border-[#262626] rounded-lg text-white text-sm focus:outline-none focus:border-[#0096FF]"
              data-testid="office-search" />
          </div>
          <select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}
            className="px-3 py-2.5 bg-[#161616] border border-[#262626] rounded-lg text-white text-sm focus:outline-none"
            data-testid="office-payment-filter">
            <option value="ALL">All</option>
            <option value="UNPAID">Unpaid</option>
            <option value="PAID">Paid</option>
            <option value="COMP">Comp</option>
          </select>
          <button onClick={generateDraw}
            className="flex items-center gap-1 px-3 py-2.5 bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30 rounded-lg text-sm font-semibold hover:bg-[#34C759]/25"
            data-testid="office-draw-btn">
            <Shuffle size={14} /> Draw
          </button>
        </div>

        {/* Entry List */}
        <div className="space-y-2" data-testid="office-entry-list">
          {sorted.map(entry => (
            <div key={entry.id} className="flex items-center gap-3 px-4 py-3 bg-[#161616] border border-[#262626] rounded-lg"
              data-testid={`office-entry-${entry.id}`}>
              <span className="text-[#0096FF] text-sm font-bold w-8 flex-shrink-0">#{entry.draw_number || '-'}</span>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-semibold truncate">{entry.rider_name}</p>
                {entry.horse_name && <p className="text-[#9E9E9E] text-xs truncate">{entry.horse_name}</p>}
              </div>
              <span className="text-[#9E9E9E] text-xs flex-shrink-0">${currentClass?.entry_fee || 0}</span>
              <button onClick={() => togglePayment(entry)}
                className={`px-3 py-1 rounded text-xs font-bold flex-shrink-0 ${paymentColors[entry.payment_status] || paymentColors.UNPAID}`}
                data-testid={`office-payment-${entry.id}`}>
                {entry.payment_status}
              </button>
            </div>
          ))}
          {sorted.length === 0 && (
            <p className="text-center text-[#525252] py-8 text-sm">{search || paymentFilter !== 'ALL' ? 'No entries match' : 'No entries yet'}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default KioskOffice;
